<!-- Settings — three choices, all of them a child's.

     How fast she writes, which surface to start on, and whether the English
     letter shows faintly under each print while learning. One stored record;
     every change saves the moment it is made. -->
<template>
  <IonPage>
    <IonContent :fullscreen="true" class="settings">
      <div class="sheet">
        <header class="head">
          <h1>Settings</h1>
        </header>

        <section class="block">
          <h2>How fast she writes</h2>
          <div class="choices" role="group" aria-label="Writing speed">
            <button
              v-for="s in SPEEDS" :key="s.label" type="button"
              :class="{ on: current.writingSpeedMs === s.ms }"
              :aria-pressed="current.writingSpeedMs === s.ms"
              @click="save({ writingSpeedMs: s.ms })"
            >{{ s.label }}</button>
          </div>
        </section>

        <section class="block">
          <h2>Start on</h2>
          <div class="choices" role="group" aria-label="Starting surface">
            <button
              v-for="s in ['sandpit', 'scroll']" :key="s" type="button"
              :class="{ on: current.defaultSurface === s }"
              :aria-pressed="current.defaultSurface === s"
              @click="save({ defaultSurface: s })"
            >{{ s }}</button>
          </div>
        </section>

        <section class="block">
          <h2>Letter hints</h2>
          <p class="note">Show the English letter faintly under each print, for learning.</p>
          <div class="choices" role="group" aria-label="Letter hints">
            <button
              type="button" :class="{ on: current.showLetterHints }"
              :aria-pressed="current.showLetterHints"
              @click="save({ showLetterHints: true })"
            >On</button>
            <button
              type="button" :class="{ on: !current.showLetterHints }"
              :aria-pressed="!current.showLetterHints"
              @click="save({ showLetterHints: false })"
            >Off</button>
          </div>
        </section>

        <p class="quiet">
          Nothing you write leaves this device. There are no accounts and
          nothing is collected.
        </p>
      </div>
    </IonContent>
  </IonPage>
</template>

<script setup>
import { computed } from 'vue';
import { IonPage, IonContent } from '@ionic/vue';
import { settings, addSetting, saveSetting } from '../store.js';

const SPEEDS = [
  { label: 'Slowly', ms: 320 },
  { label: 'Steadily', ms: 150 },
  { label: 'Quickly', ms: 70 },
];

const DEFAULTS = { writingSpeedMs: 150, defaultSurface: 'sandpit', showLetterHints: false };

const row = computed(() => settings.value.find((r) => Number(r.id) > 0) ?? null);
const current = computed(() => ({ ...DEFAULTS, ...(row.value ?? {}) }));

function save(patch) {
  if (row.value) saveSetting({ ...row.value, ...patch });
  else addSetting({ ...DEFAULTS, ...patch });
}
</script>

<style scoped>
.settings { --background: var(--fs-paper); }
.sheet {
  min-height: 100%; background: var(--fs-paper); color: var(--fs-ink);
  font-family: var(--fs-body);
  padding: calc(16px + env(safe-area-inset-top, 0px)) 0 calc(16px + env(safe-area-inset-bottom, 0px));
}
.head { padding: 0 20px; }
.head h1 { margin: 0; font-family: var(--fs-serif); font-style: italic; font-weight: 400; font-size: 28px; }
.block { padding: 18px 20px 0; }
h2 {
  margin: 0 0 10px; font-family: var(--fs-mono, inherit); font-size: 12px; font-weight: 500;
  letter-spacing: 0.1em; text-transform: uppercase;
}
.note { margin: -4px 0 10px; font-size: 14px; color: color-mix(in srgb, var(--fs-ink) 65%, transparent); }
.choices { display: flex; gap: 8px; flex-wrap: wrap; }
.choices button {
  min-height: 48px; padding: 0 18px;
  border: 1px solid color-mix(in srgb, var(--fs-sepia) 45%, transparent); border-radius: 2px;
  background: transparent; color: var(--fs-ink);
  font-family: var(--fs-body); font-size: 16px; cursor: pointer;
}
.choices button.on { background: var(--fs-sepia); color: var(--fs-cream); border-color: var(--fs-sepia); }
.quiet { margin: 26px 20px 0; font-size: 13px; line-height: 1.6; color: color-mix(in srgb, var(--fs-ink) 50%, transparent); }
</style>
